import { useMemo } from "react";
import {
  IconUser,
  IconX,
  IconClock,
  IconFileDescription,
  IconUsersMinus,
} from "@tabler/icons-react";
import m from "../../../../components/Modal/modal.module.css";
import s from "./applicants.module.css";
import Button from "../../../../components/UI/Button/button";
import { getNameInitials } from "../../../../utils/functions";

const DECISION_LABEL = {
  recommended: "Recommended",
  "selected by client": "Selected by Client",
  rejected: "Rejected",
  cancelled: "Cancelled",
  left: "Hire left",
  pending_removal: "Pending Removal",
  "not selected": "Not Selected",
  applied: "Applied",
  active: "Active",
};

const DECISION_CLASS = {
  recommended: s.decisionRecommended,
  "selected by client": s.decisionSelected,
  rejected: s.decisionRejected,
  cancelled: s.decisionCancelled,
  left: s.decisionCancelled,
  pending_removal: s.decisionPendingRemoval,
  "not selected": s.decisionNotSelected,
  applied: s.decisionSelected,
  active: s.decisionSelected,
};

const AVATAR_PALETTE = [
  { bg: "#eef2ff", fg: "#1d4ed8" }, // blue
  { bg: "#ecfdf5", fg: "#059669" }, // green
  { bg: "#fef3c7", fg: "#92400e" }, // amber
  { bg: "#fce7f3", fg: "#be185d" }, // pink
  { bg: "#f3e8ff", fg: "#7c3aed" }, // purple
  { bg: "#e0f2fe", fg: "#0369a1" }, // sky
  { bg: "#f0fdfa", fg: "#0d9488" }, // teal
  { bg: "#fef2f2", fg: "#dc2626" }, // red
  { bg: "#f1f5f9", fg: "#64748b" }, // slate
];

function getAvatarColor(seed) {
  const str = String(seed ?? "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // keep it a 32-bit int
  }
  const index = Math.abs(hash) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[index];
}

export default function ApplicantsModal({
  job,
  open,
  onClose,
  onViewDetails,
  loading,
  applicants,
}) {
  const list = useMemo(() => applicants || [], [applicants]);

  if (!open || !job) return null;

  return (
    <div
      className={m.backdrop}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`${m.card} ${m.cardLg}`}>
        <div className={m.head}>
          <div
            className={m.headIcon}
            style={{
              background: job.iconBg || "#eef2ff",
              color: job.iconFg || "#1d4ed8",
            }}
          >
            <IconUser size={22} />
          </div>
          <div className={m.headInfo}>
            <div className={m.headTitle}>{job.title} — Applicants</div>
            {!loading && (
              <div className={m.headSub}>
                {list.length} total applicant{list.length !== 1 ? "s" : ""}
              </div>
            )}
          </div>
          <button className={m.closeBtn} onClick={onClose} aria-label="Close">
            <IconX size={17} />
          </button>
        </div>

        <div className={m.body}>
          {loading && (
            <div className={s.empty}>
              <div className="flex gap-3 items-center justify-center">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#e6eaf2] border-t-transparent" />
                Fetching applicants...
              </div>
            </div>
          )}

          {!loading && list.length === 0 && (
            <div className={s.empty}>
              <IconUsersMinus size={36} />
              <p>No applicants for this job yet.</p>
            </div>
          )}

          {!loading && list.length > 0 && (
            <div className={s.list}>
              {list.map((applicant, index) => {
                const candidateNum = index + 1;
                const status = applicant?.status?.toLowerCase();
                const talentName = `${applicant?.talent?.firstName} ${applicant?.talent?.lastName}`;
                const statusKey = applicant?.status?.toLowerCase().trim();
                const avatarColor = getAvatarColor(
                  applicant.applicantId ?? candidateNum,
                );

                return (
                  <div
                    className={s.row}
                    key={applicant.applicantId ?? candidateNum}
                  >
                    <div
                      className={s.avatar}
                      style={{ background: avatarColor.bg || "#eef2ff" }}
                    >
                      <IconUser
                        size={20}
                        style={{ color: avatarColor.fg || "#1d4ed8" }}
                      />
                    </div>
                    <div className={s.info}>
                      <div className={s.name}>
                        {status === "selected by client" || status === "left"
                          ? talentName
                          : `Candidate #${candidateNum}`}
                      </div>
                      <div className={s.role}>{applicant.role}</div>
                      <div className={s.meta}>
                        <span className={s.time}>
                          <IconClock size={12} /> Applied {applicant.time}
                        </span>
                      </div>
                    </div>
                    <div className={s.right}>
                      <span
                        className={`${s.decision} ${DECISION_CLASS[statusKey] || ""}`}
                      >
                        {DECISION_LABEL[statusKey] || applicant.decision}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={m.foot}>
          <Button
            variant="ghost"
            leftIcon={<IconFileDescription size={15} />}
            onClick={() => onViewDetails(job)}
          >
            View Job Details
          </Button>
        </div>
      </div>
    </div>
  );
}
